import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Steps } from 'antd';
import classnames from 'classnames';

import { warningToast } from '../../store/slices/toaster.slice';
import AppConfig from '../../providers/AppConfig';
import { AppHeaderAdmin, ThankYouPage } from '..';
import { RequestHeader } from '../RequestHeader/RequestHeader';
import { useRequestList } from '../../context/RequestList.context';

import './FormSteps.scss';

export const FormSteps = React.memo(({ steps, translationPrefix, securityType }) => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();
  const { requestId } = useParams();

  const dispatch = useDispatch();

  const requestCompleted = useSelector((state) => state.requestMeta.requestCompleted);
  const requestSubmitted = useSelector((state) => state.requestMeta.requestSubmitted);
  const isAdmin = useSelector((state) => state.user.details.is_admin);
  const { list, isLoading: listLoading } = useRequestList();
  const resources = useSelector((state) => state.resources);

  const [currentStep, setCurrentStep] = useState(0);
  const templateRef = useRef(null);
  const loadedKeyRef = useRef(null);

  // Step navigation functions
  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      templateRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      templateRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
      templateRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const lastStep = () => {
    const lastStepIndex = steps.length - 1;
    setCurrentStep(lastStepIndex);
    templateRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Create a steps instance object to maintain compatibility with existing code
  const stepsInstance = {
    state: {
      activeStep: currentStep,
      totalSteps: steps.length,
    },
    nextStep,
    prevStep,
    goToStep,
    lastStep,
  };

  useLayoutEffect(() => {
    if (loadedKeyRef.current === (requestId ?? 'new')) {
      return;
    }

    if (requestId) {
      if (listLoading) {
        return;
      }

      // If request has already been submitted or completed, do not load the request again
      if (requestSubmitted || requestCompleted) {
        console.warn('Request already submitted or completed, skipping loading.');
        return;
      }

      const item = list.find((item) => item.case_id === requestId);

      if (!item) {
        warningToast(common('errors.cantFindRequestC'), common('errors.cantFindRequestT'));
        navigate('/list');
        return;
      }

      if (!AppConfig.editStatuses.includes(item.status)) {
        warningToast(common('errors.notEditableRequestC'), common('errors.notEditableRequestT'));
        navigate('/list');
        return;
      }

      dispatch({ type: 'request/updateRequestField', payload: item });
    } else {
      // Set security type
      if (resources.security_type && resources.security_type.data.length) {
        const type = resources.security_type.data.find(({ slug }) => slug === securityType);
        dispatch({ type: 'request/updateRequestField', payload: { security_type: type.id } });
      }
    }

    loadedKeyRef.current = requestId ?? 'new';
  }, [
    common,
    dispatch,
    list,
    navigate,
    requestId,
    resources.security_type,
    securityType,
    requestSubmitted,
    requestCompleted,
    listLoading,
  ]);

  const uiSteps = steps.map(({ no }, idx, arr) => {
    let alwaysOn = (requestCompleted && idx + 1 !== arr.length) || requestSubmitted;

    return {
      title: t(`request.${translationPrefix}.steps`, { returnObjects: true })[no],
      status: alwaysOn ? 'finish' : idx === currentStep ? 'process' : 'wait',
      key: no,
    };
  });

  // Handle step click for navigation
  const handleStepClick = (stepIndex) => {
    // Allow navigation to previous steps or completed steps
    if (stepIndex <= currentStep || requestCompleted) {
      goToStep(stepIndex);
    }
  };

  // Clone the current step component with navigation props
  const renderCurrentStep = () => {
    if (currentStep >= 0 && currentStep < steps.length) {
      return React.cloneElement(steps[currentStep].component, {
        key: currentStep,
        nextStep,
        prevStep,
        goToStep,
        lastStep,
        currentStep,
        totalSteps: steps.length,
        stepsInstance,
      });
    }
    return null;
  };

  return (
    <div
      className={classnames('steps-view', {
        'steps-view--admin': isAdmin,
      })}
      ref={templateRef}
    >
      <div className="steps-view__header">
        {isAdmin && <AppHeaderAdmin isProspectus />}
        <div className="container">
          <RequestHeader
            header={t(`request.${translationPrefix}.header`)}
            onCancel={{ label: common(`request.cancel`) }}
            stepsInstance={stepsInstance}
          />
          <div className="steps-view__steps">
            <Steps
              current={currentStep}
              titlePlacement="vertical"
              items={uiSteps}
              onChange={handleStepClick}
              className="form-steps"
            />
          </div>
        </div>
      </div>
      <div className="steps-view__wrapper">
        {requestSubmitted ? (
          <ThankYouPage link="/list" />
        ) : (
          <div className="steps-view__wizard">{renderCurrentStep()}</div>
        )}
      </div>
    </div>
  );
});

export default FormSteps;
